import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Clock, ChevronDown, ShieldCheck } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { IMAGES, CONTACT } from "@/data/site";
import { MaskedLines } from "./Reveal";
import { Crest } from "./Crest";
import { openWhatsApp } from "@/lib/wa";

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scaleBg = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative flex min-h-[100svh] items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Parallax background */}
      <motion.div style={{ y: yBg, scale: scaleBg }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.hero}
          alt=""
          className="h-full w-full object-cover"
          style={{ filter: "brightness(0.72) contrast(1.06) saturate(1.05)", objectPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/50 to-[var(--bg)]" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/70 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
      </motion.div>

      {/* Faint crest behind title */}
      <Crest className="pointer-events-none absolute left-1/2 top-1/2 h-[72vh] w-auto -translate-x-1/2 -translate-y-1/2 opacity-[0.06]" />

      <motion.div style={{ opacity }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pt-28 pb-20 md:px-10">
        <div className="mx-auto max-w-5xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-6 flex flex-wrap items-center justify-center gap-3"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-[var(--gold)]/50 px-4 py-1.5 font-sans text-[11px] uppercase tracking-[0.28em] text-[var(--gold)]">
              <ShieldCheck size={14} /> {t.halal}
            </span>
            <span className="font-sans text-[11px] uppercase tracking-[0.28em] text-[var(--text-2)]">
              {t.heroKicker}
            </span>
          </motion.div>

          <h1 className="font-serif text-7xl leading-[0.85] tracking-tight text-[var(--text)] sm:text-8xl md:text-[9rem]">
            <MaskedLines lines={[t.heroTitle1]} delay={0.35} />
            <MaskedLines
              lines={[<span key="2" className="italic gold-gradient-text">{t.heroTitle2}</span>]}
              delay={0.5}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1 }}
            className="mt-7 mx-auto max-w-lg font-sans text-base leading-relaxed text-[var(--text-2)] md:text-lg"
          >
            {t.heroTagline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-9 flex flex-wrap items-center justify-center gap-4"
          >
            <button
              onClick={() => openWhatsApp(t.waOrder)}
              data-testid="hero-order-btn"
              className="group relative overflow-hidden bg-[var(--gold)] px-8 py-4 font-sans text-sm font-semibold tracking-wide text-[var(--bg)] transition-transform hover:scale-[1.02]"
            >
              {t.heroCta}
            </button>
            <a
              href="#menu"
              data-testid="hero-menu-btn"
              className="border border-[var(--gold)]/50 px-8 py-4 font-sans text-sm tracking-wide text-[var(--gold)] transition-colors hover:bg-[var(--gold)]/10"
            >
              {t.heroCta2}
            </a>
          </motion.div>

          {/* Hours strip */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="mx-auto mt-12 flex w-fit items-center gap-3 border-t border-[var(--gold)]/20 pt-5"
          >
            <Clock size={18} className="text-[var(--gold)]" />
            <span className="font-serif text-2xl italic text-[var(--text)]">{t.open}</span>
            <span className="h-4 w-px bg-[var(--gold)]/40" />
            <span className="font-sans text-sm tracking-wide text-[var(--text-2)]">{t.hours}</span>
          </motion.div>
        </div>
      </motion.div>

      <motion.a
        href="#menu"
        style={{ opacity }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[var(--gold)]"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        aria-label="scroll"
      >
        <ChevronDown size={26} />
      </motion.a>
    </section>
  );
}
